import React from 'react';

import type { ClientOptions } from '@sentry/types';
import { useRawConfigState } from '@zillow/shopper-platform-config';
import Head from 'next/head';

export const HOPS_SENTRY_URL = 'https://js.sentry-cdn.com/75ee82966b770381fb98750a967577a3.min.js';
export const HOPS_SENTRY_SAMPLE_RATE = 0.01;

type SentryConfig = Omit<ClientOptions, 'transport' | 'integrations' | 'stackParser'>;

export const getSentryConfig = (env: string): SentryConfig => ({
    /**
     * Release version of the app. This is used to associate errors with a release.
     * Release version = package name @ git commit short hash
     */
    // eslint-disable-next-line zillow/@typescript-eslint/restrict-template-expressions
    release: `hops-homepage@${WEBPACK.GIT_HASH}`,
    /**
     * Current environment the app is being run in.
     * This is provided at runtime by Shopper Platform configuration.
     */
    environment: env,
    /**
     * Maximum number of breadcrumbs that should be sent with an exception.
     */
    maxBreadcrumbs: 50,
    /**
     * Sample rate for captured exceptions. Range is 0.0 to 1.0.
     */
    sampleRate: ['production', 'prod'].includes(env) ? HOPS_SENTRY_SAMPLE_RATE : 1,
    /**
     * Prevent false flags
     */
    ignoreErrors: ['chrome-extension://'],
});

/**
 * We're rendering this using Next.js Head so we can render the script as early as possible
 * when the page loads. This is to ensure we capture as many errors as possible.
 *
 * The loader script will only download the full Sentry SDK when an exception occurs.
 *
 * @see https://gitlab.zgtools.net/zillow/searchxp/search-page-sub-app/-/blob/main/src/sub-app/SearchPageSentry.tsx
 */

const HopsSentry = () => {
    const { env } = useRawConfigState();
    return (
        <Head>
            <script src={HOPS_SENTRY_URL} crossOrigin="anonymous"></script>
            <script
                // eslint-disable-next-line zillow/react/no-danger
                dangerouslySetInnerHTML={{
                    // eslint-disable-next-line zillow/@typescript-eslint/naming-convention
                    __html: `
                        window.Sentry.onLoad(function () {
                            const sentryConfig = ${JSON.stringify(getSentryConfig(env))};
                            window.Sentry.init(sentryConfig);
                        });
                    `,
                }}
            />
        </Head>
    );
};

export default HopsSentry;
